import type { UpcomingEvent } from "@/api/eventDiscovery";
import { isVirtualEvent } from "@/api/eventDiscovery";

// Constant confirmation links (same for every event — not per-event).
const RULES_URL =
  "https://docs.google.com/document/d/1_hZ3PpWOYAgw4nXV6Yk0hDGUFZQg4zL2ZS8Y_kAvL_o/edit?tab=t.m034xspyfgw1#heading=h.qxgq384gnjhq";

// Filesystem-safe city slug for the .ics filename, e.g. "Tel Aviv" -> "tel-aviv".
function slugifyCity(city: string): string {
  return (
    city
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "event"
  );
}

function toUtcBasic(d: Date): string {
  const p = (n: number, len = 2) => String(n).padStart(len, "0");
  return (
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`
  );
}

function toUtcDateBasic(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}`;
}

function addUtcDay(d: Date): Date {
  return new Date(d.getTime() + 24 * 60 * 60 * 1000);
}

function buildCalendarEvent(event: UpcomingEvent) {
  const virtual = isVirtualEvent(event);
  const title = virtual
    ? `Quizrun #${event.quizrun_number} — Virtual`
    : `Quizrun #${event.quizrun_number} — ${event.city}`;
  const start = new Date(event.date_start);
  const end = new Date(event.date_finish);

  const location = virtual
    ? "Online"
    : event.location_name ||
      [event.city, event.country].filter(Boolean).join(", ");

  const lines: string[] = [`You registered for ${title}!`, ""];

  if (virtual) {
    lines.push("Run and quiz any time during the event window.");
  } else {
    if (event.location_name) {
      const meetingPoint = event.location_link
        ? `${event.location_name}, ${event.location_link}`
        : event.location_name;
      lines.push(`Meeting point: ${meetingPoint}`);
    }
    if (event.route_link) {
      lines.push(`Route: ${event.route_link}`);
    }
  }

  lines.push(
    "Before you start, please take a quick look at the rules:",
    RULES_URL,
    "",
    "On the day, make sure you are signed in on the quizrun.org website.",
    "",
    "More details on the event:",
    `https://quizrun.org/events/${event.quizrun_id}`,
    "",
    "See you soon!",
    "Julia & Aidar",
    "Quizrun",
    "https://linktr.ee/quizrun",
  );

  return {
    title,
    description: lines.join("\n"),
    location,
    start,
    end,
    virtual,
  };
}

export function googleCalendarUrl(event: UpcomingEvent): string {
  const cal = buildCalendarEvent(event);
  // Virtual: multi-day all-day span over the whole window. Date-only range reads
  // as all-day/free in Google; end day is +1 (all-day end is non-inclusive).
  const dates = cal.virtual
    ? `${toUtcDateBasic(cal.start)}/${toUtcDateBasic(addUtcDay(cal.end))}`
    : `${toUtcBasic(cal.start)}/${toUtcBasic(cal.end)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: cal.title,
    dates,
    details: cal.description,
    location: cal.location,
  });
  if (cal.virtual) {
    params.set("crm", "AVAILABLE");
  }
  return `https://www.google.com/calendar/render?${params.toString()}`;
}

function escapeIcs(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

export function icsContent(event: UpcomingEvent): string {
  const cal = buildCalendarEvent(event);
  const now = toUtcBasic(new Date());
  // Virtual: multi-day all-day DATE VEVENT spanning the whole window. DTEND is
  // finish +1 (RFC 5545 all-day DTEND is non-inclusive); TRANSP:TRANSPARENT free.
  const timing = cal.virtual
    ? [
        `DTSTART;VALUE=DATE:${toUtcDateBasic(cal.start)}`,
        `DTEND;VALUE=DATE:${toUtcDateBasic(addUtcDay(cal.end))}`,
        "TRANSP:TRANSPARENT",
      ]
    : [`DTSTART:${toUtcBasic(cal.start)}`, `DTEND:${toUtcBasic(cal.end)}`];
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Quizrun//quizrun.org//EN",
    "BEGIN:VEVENT",
    `UID:quizrun-${event.event_id}@quizrun.org`,
    `DTSTAMP:${now}`,
    ...timing,
    `SUMMARY:${escapeIcs(cal.title)}`,
    `DESCRIPTION:${escapeIcs(cal.description)}`,
    `LOCATION:${escapeIcs(cal.location)}`,
    `URL:https://quizrun.org/events/${event.quizrun_id}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}

export function downloadIcs(event: UpcomingEvent): void {
  const content = icsContent(event);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `quizrun-${event.quizrun_number}-${slugifyCity(event.city)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
